import { motion } from "framer-motion";
import { useState, lazy, Suspense } from "react";

interface CodeExample {
  language: string;
  code: string;
  description?: string;
}

interface CodeExampleSectionProps {
  codeExamples: CodeExample[];
  delay?: number;
}

// Lazy load the code example
const LazyCodeExample = lazy(() => import('./LazyCodeExample'));

export default function CodeExampleSection({
  codeExamples,
  delay = 0
}: CodeExampleSectionProps) {
  const [showCode, setShowCode] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Generiert einen aussagekräftigen Buttontitel basierend auf der Sprache und Beschreibung
  const getButtonTitle = (example: CodeExample, index: number) => {
    const langMap: Record<string, string> = {
      typescript: "TypeScript",
      javascript: "JavaScript",
      scss: "SCSS/CSS",
      css: "CSS",
      html: "HTML",
      jsx: "React JSX",
      json: "JSON"
    };
    
    const langName = langMap[example.language] || example.language.toUpperCase();
    
    // Extrahiere einen kurzen Titel aus der Beschreibung oder dem Code
    let shortTitle = `Beispiel ${index + 1}`;
    if (example.description) {
      // Verwende die ersten paar Wörter der Beschreibung
      const words = example.description.split(' ').slice(0, 3).join(' ');
      shortTitle = words + (words.length < example.description.length ? '...' : '');
    } else {
      // Extrahiere aus dem Code, wenn keine Beschreibung vorhanden ist
      const firstLine = example.code.split('\n')[0];
      if (firstLine && firstLine.length > 5) {
        shortTitle = firstLine.replace(/\/\/\s*/, '').substring(0, 25);
        if (shortTitle.length >= 25) shortTitle += '...';
      }
    }
    
    return `${langName}: ${shortTitle}`;
  };

  return (
    <motion.div
      className="mb-20"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay }}
    >
      <motion.h2
        className="text-3xl md:text-4xl font-bold mb-6 text-[#88ddff]"
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: delay + 0.2 }}
      >
        Code-Beispiele
      </motion.h2>

      <div className="flex flex-wrap gap-3 mb-6">
        {!showCode ? (
          <motion.button
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#1E0B32] border-[#88ddff] border text-white shadow-glow-blue-sm"
            onClick={() => setShowCode(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Code anzeigen
          </motion.button>
        ) : (
          codeExamples.map((example, index) => (
            <motion.button
              key={index}
              className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                activeIndex === index 
                  ? "bg-[#88ddff]/20 border-[#88ddff] text-white shadow-glow-blue-sm" 
                  : "bg-[#1E0B32]/40 border-[#88ddff]/50 text-white/80 hover:bg-[#1E0B32]/60"
              }`}
              onClick={() => setActiveIndex(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={example.description || ""}
            >
              {getButtonTitle(example, index)}
            </motion.button>
          ))
        )}
      </div>

      {showCode && (
        <Suspense fallback={<div className="h-64 bg-[#1E0B32]/30 rounded-xl animate-pulse" />}>
          <LazyCodeExample codeExample={codeExamples[activeIndex]} />
        </Suspense>
      )}
    </motion.div>
  );
}